/**
 * The standing orders block (03 §2, 10 §1.1) and, for when it is folded away, the ONE line a player needs at a
 * glance. Found on screen, mission 36: six lines of prose filling a quarter of the display for the whole
 * mission. The block grew a line at a time and nobody looked at the total, so every line now carries a rank -
 * what can lose the mission, what will waste his explosives, and the rest - and the panel keeps the best one.
 *
 * Pure, and driven by the rule data (kind, effect, the rule's own words), never by which archetype this is.
 */

import type { MissionDef, ObjectiveRule } from '../../sim/types';
import { chargesOrders } from './missionCharges';
import {
  capturedOrders, dogsOrders, durabilityOrders, intelOrders, modifierOrders, wallsOrders, weakSideOrders, weatherOrders,
} from './missionConditions';
import { ALERT_LOSES, ALERT_ORDERS, alertOrders, TIMED_ORDERS, timedLoses } from './missionRuleText';
import { uniformOrders } from './missionUniform';
import { fontPx, lineHeightPx, ordersWidth, wrapRows } from './textSize';

/** How badly a line is needed at a glance, lowest first: the collapsed panel keeps the best one it has. */
export const LOSING = 0;
/** Not a loss, but it wastes his explosives: the face that opens a target, what will open it at all. */
export const WASTED = 1;
/** Worth reading once at the start: how the ground works, a modifier, the uniform route. */
export const PLAIN = 2;

/** How tall the open block may stand, logical px: the rest of the panel needs the clock and the extraction line. */
export const ORDERS_MAX_PX = 120;

export interface OrdersLine {
  text: string;
  /** `LOSING`, `WASTED` or `PLAIN`. */
  rank: number;
}

export const ruleOfType = <T extends ObjectiveRule['type']>(
  def: MissionDef,
  type: T,
): Extract<ObjectiveRule, { type: T }> | undefined =>
  def.objective.rules.find((r): r is Extract<ObjectiveRule, { type: T }> => r.type === type);

const ORIGINAL_EFFECTS: ReadonlyArray<string> = [
  'jetsScramble', 'prisonersMoved', 'demolition', 'officerFlees', 'reinforceTarget',
];

/** The 1987 missions' one standing order: the first of their rules that can lose the mission (03 §2). */
function originalOrders(def: MissionDef): OrdersLine | null {
  if (ruleOfType(def, 'bombsHoldUntilMidnight')) {
    // Mission 12's dump, or (10 §2, mission 34) several marked targets and a column that sets them off.
    if (def.objective.kind !== 'plantAtTargets') {
      return { text: 'BOMBS GO OFF AT MIDNIGHT: PLANT ONE AT THE DUMP, LEAVE UNSEEN', rank: LOSING };
    }
    // A not-seen rule with words of its own gets its own line below; without, the order carries it.
    const seen = ruleOfType(def, 'notSeenNear');
    const unseen = seen !== undefined && seen.orders === undefined ? ', AND DO NOT BE SEEN NEAR THEM' : '';
    return { text: `${chargesOrders(def)}${unseen}`, rank: LOSING };
  }
  const consequence = ruleOfType(def, 'alertConsequence');
  if (consequence && ORIGINAL_EFFECTS.includes(consequence.effect)) {
    const text = consequence.orders ?? ALERT_ORDERS[consequence.effect];
    if (text !== null) return { text, rank: ALERT_LOSES[consequence.effect] ? LOSING : PLAIN };
  }
  if (ruleOfType(def, 'signal')) {
    return {
      text: 'LIE LOW UNTIL THE SIGNAL, THEN MAKE NOISE. NO RECALL: THE OSPREY COMES AT 0:00 - BE AT THE BORDER THEN',
      rank: LOSING,
    };
  }
  const seen = ruleOfType(def, 'notSeenNear');
  if (seen) return { text: seen.orders ?? 'DO NOT BE SEEN NEAR THE TARGET', rank: LOSING };
  const quiet = ruleOfType(def, 'quietUntilZone');
  if (quiet) {
    return quiet.breach === 'fail'
      ? { text: 'STAY SILENT UNTIL YOU ARE INSIDE', rank: LOSING }
      : { text: 'QUIET UNTIL YOU ARE INSIDE SCORES HIGHER', rank: PLAIN };
  }
  return null;
}

/** One line per rule of the objective's own, after the originals' first line. */
function ruleOrders(def: MissionDef): OrdersLine[] {
  const lines: OrdersLine[] = [];
  // A quiet rule is covered by an alert rule that ends at the same fence ("...UNTIL YOU ARE INSIDE"); the
  // midnight order already says "unseen". Everything else that can lose the mission gets a line of its own.
  const fenced = def.objective.rules.some((r) => r.type === 'alertConsequence' && r.unlessInsideZone !== undefined);
  const midnight = ruleOfType(def, 'bombsHoldUntilMidnight') !== undefined;
  for (const r of def.objective.rules) {
    if (r.type === 'alertConsequence') {
      const text = ORIGINAL_EFFECTS.includes(r.effect) ? (r.orders ?? ALERT_ORDERS[r.effect]) : alertOrders(r);
      if (text !== null) lines.push({ text, rank: ALERT_LOSES[r.effect] ? LOSING : PLAIN });
    } else if (r.type === 'timedEvent') {
      const text = r.orders ?? TIMED_ORDERS[r.effect](def, r);
      if (text !== null) lines.push({ text, rank: timedLoses(def, r) ? LOSING : PLAIN });
    } else if (r.type === 'notSeenNear') {
      const text = r.orders ?? (midnight ? null : 'DO NOT BE SEEN NEAR THE TARGET');
      if (text !== null) lines.push({ text, rank: LOSING });
    } else if (r.type === 'quietUntilZone' && r.breach === 'fail' && !fenced) {
      lines.push({ text: 'STAY SILENT UNTIL YOU ARE INSIDE', rank: LOSING });
    }
  }
  return lines;
}

/** Every standing order with its rank: the originals' line, the expansion's, then the ground and conditions. */
export function ordersBlock(def: MissionDef): OrdersLine[] {
  const lines: OrdersLine[] = [];
  const first = originalOrders(def);
  if (first !== null) lines.push(first);
  lines.push(...ruleOrders(def));

  const objective = def.objective;
  const item = objective.questItem;
  if (item) {
    lines.push({
      text: `THE ${item.name} RIDES IN POD ${'ABC'[item.pod] ?? ''}: LOSE THAT POD AND THE MISSION IS LOST`,
      rank: LOSING,
    });
  }
  if (objective.dwellUnseen === true) lines.push({ text: 'BEING SEEN WHILE YOU ARE AT IT THROWS THE COUNT AWAY', rank: PLAIN });
  // Tag Recovery (04 §2.3). The briefing says his marker stands somewhere on this ground and then
  // nothing in the mission ever mentioned him again - the one optional objective with no in-flight
  // word at all. It is on the strip map now, in amber, and this says whose it is.
  if (def.dogTags !== undefined) {
    lines.push({ text: `OPTIONAL: ${def.dogTags.name.toUpperCase()}'S TAGS - HIS MARKER IS AMBER ON THE MAP`, rank: PLAIN });
  }
  // A hold is lost by walking out of the zone and nothing else: that is its one rule.
  if (objective.kind === 'holdZone' && objective.duringWindow !== true) {
    lines.push({ text: 'LEAVING THE ZONE STOPS THE CLOCK', rank: LOSING });
  }
  if (ruleOfType(def, 'recall')?.mode === 'disabled' && !ruleOfType(def, 'signal')) {
    lines.push({ text: 'RECALL IS DISABLED', rank: PLAIN });
  }
  // The ground and the conditions (missionConditions.ts): what opens a target, modifiers that change the rules.
  const weak = weakSideOrders(def);
  if (weak !== null) lines.push({ text: weak, rank: WASTED });
  const durability = durabilityOrders(def);
  if (durability !== null) lines.push({ text: durability, rank: WASTED });
  const weather = weatherOrders(def);
  if (weather !== null) lines.push({ text: weather, rank: PLAIN });
  // ROUND8: how a dog works, so PLAIN - it never displaces the line a mission folds to - but kept before the cards
  // and the walls when the panel runs out of height: it is what gets him found. The briefing names the teams too.
  const dogs = dogsOrders(def);
  if (dogs !== null) lines.push({ text: dogs, rank: PLAIN });
  for (const text of modifierOrders(def)) lines.push({ text, rank: PLAIN });
  // What the cards he bought actually did to this mission (04 §8): the whole point of paying for them.
  for (const text of intelOrders(def)) lines.push({ text, rank: PLAIN });
  const captured = capturedOrders(def);
  if (captured !== null) lines.push({ text: captured, rank: PLAIN });
  const uniform = uniformOrders(def);
  if (uniform !== null) lines.push({ text: uniform, rank: PLAIN });
  // ROUND7: how the ground works, so PLAIN, and last - it never displaces the line a mission folds to. The
  // briefing carries it too, so the panel may drop it when a crowded mission runs out of height.
  const walls = wallsOrders(def);
  if (walls !== null) lines.push({ text: walls, rank: PLAIN });

  const seen = new Set<string>();
  const unique = lines.filter((line) => (seen.has(line.text) ? false : seen.add(line.text) !== undefined));
  return withinPanel(unique);
}

/**
 * The open block has a height, not a line count: mine-road's five orders wrap to nine rows at x2 text and run
 * off the panel. Measure it the way the panel will lay it out, at the largest text a player can choose, and
 * drop lines from the BOTTOM of the ranking until it fits - a rule that can lose the mission is never cut to
 * make room for one that cannot. Anything dropped is still in the briefing.
 */
function withinPanel(lines: OrdersLine[]): OrdersLine[] {
  const width = ordersWidth(2, true);
  const font = fontPx('orders', 2);
  const rowHeight = lineHeightPx('orders', 2);
  const rows = (kept: OrdersLine[]): number => wrapRows(kept.map((l) => l.text).join('\n'), width, font);
  if (rows(lines) * rowHeight <= ORDERS_MAX_PX) return lines;

  const byRank = [...lines].sort((a, b) => a.rank - b.rank);
  let kept = byRank;
  while (kept.length > 1 && rows(kept) * rowHeight > ORDERS_MAX_PX) kept = kept.slice(0, -1);
  const keep = new Set(kept);
  return lines.filter((line) => keep.has(line));
}

/** Every standing order, one per rule that can lose the mission, then the rest. */
export function ordersLines(def: MissionDef): string[] {
  return ordersBlock(def).map((line) => line.text);
}

/** The whole block, a line each, as the panel shows it at the start of the mission; null when there are none. */
export function ordersLine(def: MissionDef): string | null {
  const lines = ordersLines(def);
  return lines.length > 0 ? lines.join('\n') : null;
}

/**
 * The one line the panel keeps once the orders fold away: the first rule that can lose this mission, failing
 * that the first that would waste his explosives, failing that the first line there is. Null: nothing to say.
 */
export function keyOrdersLine(def: MissionDef): string | null {
  const lines = ordersBlock(def);
  let best: OrdersLine | null = null;
  for (const line of lines) if (best === null || line.rank < best.rank) best = line;
  return best?.text ?? null;
}
